import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { BreadcrumbService } from '../../domain/services/breadcrumb/breadcrumb.service';
import { CasheringService } from '../../domain/services/cashering/cashering.service';
import { ReturnSearchCriteria, ReturnStatus, ReturnSummaryDto } from '../../domain/models/return.model';
import { toCsv, downloadCsv } from '../reports/payment-reports/report-export.util';

/**
 * Advanced query: filter tax returns across all accounts by customer, status, period,
 * frequency, due-date range, and balance, then export the results to CSV. Distinct from
 * the Tasks list (which only shows approval workflow items).
 */
@Component({
  selector: 'app-query',
  standalone: true,
  imports: [
    CommonModule, CurrencyPipe, DatePipe, FormsModule,
    MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatDatepickerModule, MatNativeDateModule, MatProgressBarModule, MatTableModule,
  ],
  templateUrl: './query.component.html',
  styleUrl: './query.component.css',
})
export class QueryComponent implements OnInit {
  readonly statusOptions = Object.values(ReturnStatus);
  readonly frequencyOptions = ['MONTHLY', 'QUARTERLY', 'ANNUAL', 'SEASONAL'];

  // Criteria
  customerId = '';
  period = '';
  frequency = '';
  statuses: ReturnStatus[] = [];
  dueDateFrom: Date | null = null;
  dueDateTo: Date | null = null;
  hasBalanceDue = ''; // '' any | 'true' | 'false'

  results: ReturnSummaryDto[] = [];
  displayedColumns = ['accountName', 'customerId', 'period', 'frequency', 'dueDate', 'status', 'totalAmountDue', 'balanceDue'];
  searched = false;
  loading = false;
  error: string | null = null;

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly cashering: CasheringService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'search', url: '/app/query', label: 'Query' },
    ]);
  }

  search(): void {
    this.loading = true;
    this.error = null;
    this.searched = true;
    this.cashering.searchReturns(this.buildCriteria()).subscribe({
      next: (page) => {
        this.results = page.content ?? [];
        this.loading = false;
      },
      error: () => {
        this.error = 'Search failed. Please adjust the criteria and try again.';
        this.results = [];
        this.loading = false;
      },
    });
  }

  reset(): void {
    this.customerId = '';
    this.period = '';
    this.frequency = '';
    this.statuses = [];
    this.dueDateFrom = null;
    this.dueDateTo = null;
    this.hasBalanceDue = '';
    this.results = [];
    this.searched = false;
    this.error = null;
  }

  exportCsv(): void {
    if (!this.results.length) return;
    const header = ['Account', 'Customer ID', 'Period', 'Frequency', 'Due date', 'Status', 'Amount due', 'Balance due'];
    const rows = this.results.map((r) => [
      r.accountName ?? '',
      r.customerId ?? '',
      r.period ?? '',
      r.frequency ?? '',
      r.dueDate ?? '',
      r.status ?? '',
      String(r.totalAmountDue ?? 0),
      String(r.balanceDue ?? 0),
    ]);
    downloadCsv('returns-query.csv', toCsv(header, rows));
  }

  private buildCriteria(): ReturnSearchCriteria {
    return {
      customerId: this.customerId.trim() || null,
      period: this.period.trim() || null,
      frequency: this.frequency || null,
      statuses: this.statuses.length ? this.statuses : null,
      dueDateFrom: this.toIso(this.dueDateFrom),
      dueDateTo: this.toIso(this.dueDateTo),
      hasBalanceDue: this.hasBalanceDue === '' ? null : this.hasBalanceDue === 'true',
    };
  }

  /** Date -> yyyy-MM-dd (local, no timezone shift). */
  private toIso(d: Date | null): string | null {
    if (!d) return null;
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
